import {
  Autocomplete,
  AutocompleteInputChangeReason,
  AutocompleteRenderGroupParams,
  FilterOptionsState,
  SxProps,
} from "@mui/material";
import { HTMLAttributes, MouseEvent, ReactNode, SyntheticEvent } from "react";
import CommonTextField from "../../commonTextField/CommonTextField";
import AutocompleteChip from "../../customAutocomplete/AutocompleteChip";

type Props<T> = {
  value: T[];
  onChange: (value: T[] | null) => void;
  options: T[];
  getOptionLabel: (option: T) => string;
  isOptionEqualToValue?: (option: T, value: T) => boolean;
  multiple?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
  limitTags?: number;
  size?: "small" | "medium";
  sx?: SxProps;
  renderOption?: (props: HTMLAttributes<HTMLLIElement>, option: T) => ReactNode;
  renderInput?: (params: any) => ReactNode;
  disableCloseOnSelect?: boolean;
  blurOnSelect?: boolean;
  loading?: boolean;
  loadingText?: ReactNode;
  placeholder?: string;
  error?: boolean;
  helperText?: ReactNode;
  required?: boolean;
  groupBy?: (option: T) => string;
  renderGroup?: (params: AutocompleteRenderGroupParams) => ReactNode;
  onInputChange?: (
    event: SyntheticEvent,
    value: string,
    reason: AutocompleteInputChangeReason,
  ) => void;
  filterOptions?: (options: T[], state: FilterOptionsState<T>) => T[];
  noOptionsText?: ReactNode;
  inputValue?: string;
  onClose?: () => void;
  getOptionLabelForChip?: (option: T) => string | ReactNode;
  getOptionDisabled?: (option: T) => boolean;
  disableClearable?: boolean;
};

const AutocompleteBase = <T,>({
  value,
  onChange,
  options,
  getOptionLabel,
  isOptionEqualToValue,
  multiple = true,
  disabled = false,
  fullWidth = true,
  limitTags,
  size = "small",
  sx = {},
  renderOption,
  renderInput,
  disableCloseOnSelect,
  blurOnSelect,
  loading,
  loadingText,
  placeholder,
  error,
  helperText,
  required,
  groupBy,
  renderGroup,
  onInputChange,
  filterOptions,
  noOptionsText,
  inputValue,
  onClose,
  getOptionLabelForChip,
  getOptionDisabled,
  disableClearable,
}: Props<T>) => {
  const defaultRenderInput = (params: any) => (
    <CommonTextField
      {...params}
      size={size}
      placeholder={placeholder}
      error={error}
      helperText={helperText}
      required={required}
    />
  );

  const getChipLabel = (option: T): string | ReactNode => {
    if (getOptionLabelForChip) {
      return getOptionLabelForChip(option);
    }
    return getOptionLabel(option);
  };

  return (
    // <OptionType, Multiple, DisableClearable, FreeSolo>
    <Autocomplete<T, boolean, boolean | undefined, undefined>
      multiple={multiple}
      fullWidth={fullWidth}
      disabled={disabled}
      disableCloseOnSelect={disableCloseOnSelect ?? !!multiple}
      blurOnSelect={blurOnSelect ?? (multiple ? false : undefined)}
      options={options}
      value={multiple ? (value || []) : (value && value.length > 0 ? value[0] : null)}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={isOptionEqualToValue}
      onChange={(_, newValue) => {
        if (multiple) {
          onChange(newValue as T[]);
        } else {
          onChange(newValue ? [newValue as T] : null);
        }
      }}
      limitTags={limitTags}
      loading={loading}
      loadingText={loadingText}
      groupBy={groupBy}
      renderGroup={renderGroup}
      renderOption={renderOption}
      renderInput={renderInput || defaultRenderInput}
      renderTags={
        multiple
          ? (value, getTagProps) =>
              value.map((option, index) => {
                const { key, onDelete, ...restProps } = getTagProps({ index });
                const handleDelete = (event: MouseEvent) => {
                  // Stop propagation immediately to prevent event from reaching "+N" chip
                  // https://github.com/mui/material-ui/issues/42432 -> fixed in MUI v6.xx
                  event.stopPropagation();
                  event.preventDefault();
                  // Call original onDelete if it exists
                  if (onDelete) {
                    onDelete(event);
                  }
                };
                return (
                  <AutocompleteChip
                    key={key}
                    label={getChipLabel(option)}
                    onDelete={handleDelete}
                    {...restProps}
                  />
                );
              })
          : undefined
      }
      onInputChange={onInputChange}
      filterOptions={filterOptions}
      noOptionsText={noOptionsText}
      inputValue={inputValue}
      onClose={onClose}
      getOptionDisabled={getOptionDisabled}
      disableClearable={disableClearable}
      sx={{
        "& :hover .MuiAutocomplete-input, & .Mui-focused .MuiAutocomplete-input":
          {
            minWidth: "30px",
          },
        ...sx,
      }}
    />
  );
};

export default AutocompleteBase;
